import { apiService } from '@shared/services/apiServices';
import type {
  ListServiceTypesParams,
  ListServiceTypesResponse,
  ServiceTypeListItem,
} from '../types/serviceTypeTypes';
import serviceTypeEndpoints from './serviceTypeEndpoints';

export const listServiceTypesApiService = {
  listServiceTypes: async (
    params?: ListServiceTypesParams
  ): Promise<ListServiceTypesResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.status) {
      queryParams.append('status', params.status);
    }
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const fallbackPage = params?.page ?? 1;
    const fallbackLimit = params?.limit ?? 10;

    const queryString = queryParams.toString();
    const endpoint = `${serviceTypeEndpoints.SERVICE_TYPES}${queryString ? `?${queryString}` : ''}`;
    const response = await apiService.getAPI(endpoint, {
      skipGlobalErrorRedirect: true,
    });

    if (response.error) {
      if (response.status === 403) {
        const err = new Error('Forbidden') as Error & { status: number };
        err.status = 403;
        throw err;
      }
      return {
        data: [],
        totalCount: 0,
        page: fallbackPage,
        limit: fallbackLimit,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }

    const payload = (response.data ?? {}) as Record<string, unknown>;
    const list = Array.isArray(payload['data'])
      ? (payload['data'] as ServiceTypeListItem[])
      : [];
    const pagination = (payload['pagination'] ?? payload) as Record<
      string,
      unknown
    >;

    const page = Number(pagination['page'] ?? fallbackPage);
    const limit = Number(pagination['limit'] ?? fallbackLimit);
    const totalCount = Number(
      pagination['totalCount'] ?? pagination['total_count'] ?? list.length
    );
    const totalPages = Number(
      pagination['totalPages'] ??
        pagination['total_pages'] ??
        (limit > 0 ? Math.ceil(totalCount / limit) : 0)
    );
    const hasNextPage =
      typeof pagination['hasNextPage'] === 'boolean'
        ? (pagination['hasNextPage'] as boolean)
        : typeof pagination['has_next_page'] === 'boolean'
          ? (pagination['has_next_page'] as boolean)
          : page < totalPages;
    const hasPreviousPage =
      typeof pagination['hasPreviousPage'] === 'boolean'
        ? (pagination['hasPreviousPage'] as boolean)
        : typeof pagination['has_previous_page'] === 'boolean'
          ? (pagination['has_previous_page'] as boolean)
          : page > 1;

    return {
      data: list,
      totalCount,
      page,
      limit,
      totalPages,
      hasNextPage,
      hasPreviousPage,
    };
  },
};
